import React, { useMemo, useState } from "react";

// Start-encounter gate: confirms the dictating provider (NPI) and the date of service
// before capture begins.
//  • NPI: if the client has exactly one provider we don't ask — it's shown read-only.
//    With more than one, a dropdown (name · NPI · taxonomy) lets the clinician pick.
//  • DOS: defaults to today, editable to today or any past date (never the future).
// Local calendar date (YYYY-MM-DD) — NOT UTC, so the default/max DOS matches the
// provider's wall-clock day instead of rolling back a day ahead of UTC.
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const taxOf = (p) => {
  const t = (p?.taxonomies || []).find((x) => x.primary) || (p?.taxonomies || [])[0];
  return t ? (t.desc || t.code || "") : "";
};

export default function EncounterGate({ providers = [], defaultNpi = "", defaultDos = "", onStart, onCancel }) {
  const list = Array.isArray(providers) ? providers : [];
  const firstNpi = defaultNpi || list[0]?.npi_number || "";
  const [npi, setNpi] = useState(firstNpi);
  const [dos, setDos] = useState(defaultDos || today());
  const single = list.length <= 1;
  const sole = list[0];
  const max = today();
  const future = dos && dos > max;

  const label = useMemo(() => {
    const p = list.find((x) => x.npi_number === npi) || sole;
    if (!p) return "";
    const tx = taxOf(p);
    return `${p.provider_name || "Provider"} · NPI ${p.npi_number}${tx ? " · " + tx : ""}`;
  }, [npi, list, sole]);

  const start = () => {
    if (future) return;
    const p = list.find((x) => x.npi_number === npi) || sole;
    onStart({ npi: npi || undefined, date_of_service: dos || max, provider_name: p ? (p.provider_name || "") : "" });
  };

  return (
    <div
      data-testid="enc-gate-overlay"
      style={{ position: "absolute", inset: 0, zIndex: 40, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(20,20,18,.28)", backdropFilter: "blur(2px)", fontFamily: "Inter, system-ui, sans-serif" }}
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        data-testid="enc-gate"
        className="rvq-fade-in"
        style={{ width: 420, maxWidth: "92vw", background: "#fff", borderRadius: 14, boxShadow: "0 24px 60px rgba(0,0,0,.22)", border: "1px solid #ECECEF", padding: "22px 22px 18px" }}
      >
        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase", color: "#9B9B97" }}>Start encounter</div>
        <h2 style={{ margin: "4px 0 2px", fontSize: 18, fontWeight: 700, color: "#141412" }}>Confirm provider &amp; date</h2>
        <p style={{ margin: "0 0 18px", fontSize: 12.5, color: "#6C6C69", lineHeight: 1.45 }}>
          The claim is filed to this provider and date of service.
        </p>

        {/* Provider / NPI */}
        <label style={{ display: "block", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", color: "#9B9B97", marginBottom: 6 }}>Dictating provider</label>
        {single ? (
          <div
            data-testid="enc-gate-provider-readonly"
            style={{ fontSize: 13.5, color: sole ? "#141412" : "#9B9B97", fontWeight: 600, padding: "9px 12px", border: "1px solid #ECECEF", borderRadius: 9, background: "#FAFAFA", marginBottom: 16 }}
          >
            {sole ? label : "No provider linked to this client"}
          </div>
        ) : (
          <select
            data-testid="enc-gate-provider-select"
            value={npi}
            onChange={(e) => setNpi(e.target.value)}
            style={{ width: "100%", fontSize: 13.5, color: "#141412", padding: "9px 12px", border: "1px solid #D6D6DA", borderRadius: 9, background: "#fff", marginBottom: 16, cursor: "pointer" }}
          >
            {list.map((p) => {
              const tx = taxOf(p);
              return <option key={p.npi_number} value={p.npi_number}>{`${p.provider_name || "Provider"} · NPI ${p.npi_number}${tx ? " · " + tx : ""}`}</option>;
            })}
          </select>
        )}

        {/* Date of service */}
        <label style={{ display: "block", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", color: "#9B9B97", marginBottom: 6 }}>Date of service</label>
        <input
          data-testid="enc-gate-dos"
          type="date"
          value={dos}
          max={max}
          onChange={(e) => setDos(e.target.value)}
          style={{ width: "100%", fontSize: 13.5, color: "#141412", padding: "9px 12px", border: `1px solid ${future ? "#C0392B" : "#D6D6DA"}`, borderRadius: 9, background: "#fff", marginBottom: future ? 6 : 20, fontFamily: "inherit" }}
        />
        {future ? <div data-testid="enc-gate-dos-error" style={{ fontSize: 11.5, color: "#C0392B", marginBottom: 14 }}>The date of service can’t be in the future.</div> : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            data-testid="enc-gate-cancel"
            onClick={onCancel}
            style={{ fontSize: 13, fontWeight: 600, color: "#6C6C69", background: "transparent", border: "1px solid #ECECEF", borderRadius: 9, padding: "9px 16px", cursor: "pointer" }}
          >Cancel</button>
          <button
            type="button"
            data-testid="enc-gate-start"
            onClick={start}
            disabled={future}
            style={{ fontSize: 13, fontWeight: 700, color: "#fff", background: future ? "#B9B9BE" : "#141412", border: "none", borderRadius: 9, padding: "9px 18px", cursor: future ? "not-allowed" : "pointer" }}
          >Start encounter</button>
        </div>
      </div>
    </div>
  );
}
